import React, { useRef, useEffect } from "react";

import "./Disclosure.css";

const TermsAndConditions = ({ onScrollToBottom }) => {
  const termsAndConditionsRef = useRef(null);

  useEffect(() => {
    const content = termsAndConditionsRef.current;

    if (content) {
      const isScrollable = content.scrollHeight > content.clientHeight;

      // If not scrollable, enable checkbox by default
      if (!isScrollable) {
        onScrollToBottom();
      } else {
        const handleScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = content;
          if (scrollTop + clientHeight >= scrollHeight - 5) {
            onScrollToBottom();
          }
        };

        content.addEventListener("scroll", handleScroll);

        return () => content.removeEventListener("scroll", handleScroll);
      }
    }
  }, [onScrollToBottom]);

  return (
    <div
      className={"purchase-text purchase-content"}
      ref={termsAndConditionsRef}
    >
      <br />

      <div className={"purchase-body"}>
        <div className={"purchase-section"}>
          <div>
            <p>
              <b>1. General.</b> These <a href="https://www.sarlit.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> and our Privacy
              Policy (collectively, the “Terms”) govern: (i) your use of and
              access to www.sarlit.com, the SAR Platform and all other websites,
              landing pages, features, apps, technologies, accounts, platforms
              and software offered by us (collectively, “Websites”); and (ii)
              the purchase, sale and use of any subscriptions offered by us on
              such Websites, specifically including, without limitation,
              subscriptions for a non-exclusive license to ACE Alerts and the
              contents/data thereof (such subscriptions are referred to
              collectively as “Products”). By using or accessing any of our
              Websites or by purchasing or using any of our Products, you agree
              to be legally bound by the Terms. If you have any questions or
              concerns regarding the Terms, our Websites or our Products, please
              contact us by email at info@sarlit.com.
            </p>
            <p>
              <b>2. Definitions.</b> SAR, LLC, a Maryland limited liability
              company, and its affiliates, successors, agents and assigns are
              collectively referred to in the Terms as “we”, “us”, “our” or
              “SAR”. The individual using or accessing any of our Websites or
              purchasing or using any of our Products is referred to in the
              Terms as “you”, “your” or “User”. Certain other capitalized words
              are defined throughout the text of the Terms and shall have the
              meanings respectively ascribed to them.
            </p>
            <p>
              <b>3. User Duties.</b> You must be at least 18 years of age to use
              or access our Websites, purchase or use our Products and/or set up
              an account with us. When you set up an account with us, you must:
              (1) provide accurate information about yourself (and keep such
              information up to date); (2) create only one account (your own);
              (3) not share your password, give others access to your account or
              transfer your account to anyone else; (4) not be under 18 years
              old; and (5) not be the holder of an account that was previously
              disabled or suspended by us. You represent and warrant that you
              are not prohibited from receiving/using/accessing/purchasing our
              Websites and/or Products under applicable laws. You further
              represent and warrant that any purchases of our Products are for
              your own personal use and not for resale or export. We reserve the
              right, in our sole and absolute discretion, with or without notice
              and for any or no reason, to limit purchase quantities, restrict
              or prohibit access and use of our Websites, refuse sales and/or
              deactivate or suspend accounts or subscriptions. In addition, you
              agree not to engage in (or facilitate/support others engaging in)
              the following conduct: (1) using our Websites to do or share
              anything that (a) violates the Terms, (b) is unlawful, misleading,
              discriminatory or fraudulent, (c) infringes or violates someone
              else's rights, including their intellectual property rights, or
              (d) is offensive, violent or inappropriate, as determined within
              our sole and absolute discretion; (2) uploading viruses or
              malicious code or do anything that could disable, overburden, or
              impair the proper working or appearance of our Websites; or (3)
              accessing or collecting data from our Websites using automated
              means (without our prior written permission) or attempting to
              access data you do not have explicit permission to access. We can
              suspend or terminate your account and/or subscription, and
              prohibit you from using our Websites, at any time for any or no
              reason, within our sole and absolute discretion and with or
              without notice. We can use, without any restriction, any
              feedback/suggestions about our Websites or Products that you
              provide without any obligation to compensate you or keep your
              feedback/suggestions confidential. You authorize us to record our
              telephone and written conversations with you for verification and
              quality control purposes.
            </p>
            <p>
              <b>4. Order Placement.</b> You can place an order for purchase of
              our Products by checking out through your account with us. We are
              not responsible for any inaccurate information you provide at
              checkout or in connection with your account or Product
              subscription.
            </p>
            <p>
              <b>5. Intellectual Property.</b> Any content on or in our Websites
              or Products covered by intellectual property rights is owned,
              controlled and/or licensed by us or third parties (for example,
              images, designs, logos, texts, code, videos or sounds), and we or
              such third parties (as applicable) retain all rights, ownership
              and control with respect to such content.
            </p>
            <p>
              <b>6. Advertisement.</b> You agree that we may utilize web-based
              tracking tools and software (for example, Google Analytics or
              other analytic service providers) to analyze your activity on our
              Websites. You further agree that we may use your email address
              and/or phone number to confirm account activity and send you
              emails and/or texts regarding advertisements, your purchases, our
              Websites and Products and/or third-party products and services.
              You will have the ability to opt out of marketing emails and
              texts.
            </p>
            <p>
              <b>7. Data.</b> You agree that any and all data (including
              personal data) deriving from your access or use of our Websites
              and/or purchase or use of our Products is our property and can be
              used by us without any restrictions. Examples of data include, but
              are not limited to, your personal data (such as your name, email,
              phone number, address, employer and job title), your current/past
              usage of our Websites, content, communications and other
              information you provide when you use or access our Websites,
              purchase or use our Products or sign up for an account with us. To
              learn more about how your data is used, please review our Privacy
              Policy.
            </p>
            <p>
              <b>8. Cookies.</b> Cookies are small pieces of text used to store
              information on web browsers. Cookies are used to store and receive
              identifiers and other information on computers, phones, and other
              devices. Other technologies, including data we store on your web
              browser or device, identifiers associated with your device, and
              other software, are used for similar purposes. In the Terms, we
              refer to all of these technologies as “Cookies.” You consent to
              our use of Cookies, within our sole and absolute discretion, for
              any lawful purpose. You further consent to the use of Cookies on
              our Websites by third parties. To learn more about how Cookies are
              used, please review our <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
            <p>
              <b>9. Survival.</b> Unless expressly stated otherwise, the Terms
              will remain in effect both during and after your use or access of
              our Websites, purchase or use of our Products and/or account or
              subscription sign up, deletion, termination or suspension. The
              provisions of the Terms will inure to the benefit of us and our
              affiliates, successors and assigns, as well as the officers,
              directors and employees of each of these entities and us. For the
              avoidance of doubt, each and every duty, obligation, promise,
              indemnity and covenant of User contained in the Terms, and each
              representation and warranty made by User, shall expressly survive
              suspension, termination, deletion or expiration of User’s
              subscription or account indefinitely.
            </p>
            <p>
              <b>10. ACE Alert Subscription.</b> Through your account with us,
              you will have the ability to obtain an ACE Alert subscription at
              one of the following subscription levels:
            </p>
            <p>
              <b>10.1. ACE Alert Subscription – Trial:</b> Select this option to
              receive a single ACE Alert on one of the ten selected
              publicly-traded companies on the NYSE or NASDAQ at no cost. This
              trial subscription will provide you with the first ACE Alert that
              is triggered for any of the selected ten public companies. Upon
              receiving the first ACE Alert, the trial subscription will end,
              and you will have the option to continue receiving ACE Alerts by
              paying the annual subscription fee for an executive subscription
              (described below).
            </p>
            <p>
              <b>10.2. ACE Alert Subscription – Executive:</b> Select this
              option to receive ACE Alerts for up to ten publicly-traded
              companies on the NYSE or NASDAQ for the annual subscription fee of
              $4,000 per company. This executive subscription will provide you
              with ACE Alerts that are triggered for the selected companies
              during a full year beginning on the first trading day after
              signing-up for your ACE Alert subscription. This executive
              subscription will also provide you with limited access to the SAR
              Platform to attain: (i) the SAR Risk Dashboard for the selected
              company flagged with an ACE Alert, which presents all High Risk
              Adverse Corporate Events and the corresponding market
              capitalization losses during the preceding two years; and (ii) the
              SAR Risk Score for each selected company. NO REFUNDS OF ANY
              SUBSCRIPTION FEES WILL BE ISSUED UPON TERMINATION, SUSPENSION,
              DELETION OR EXPIRATION OF YOUR SUBSCRIPTION OR ACCOUNT BY YOU OR
              SAR FOR ANY OR NO REASON.
            </p>
            <p>
              <b>11. ACE Alerts Generally.</b> ACE Alerts are delivered via
              e-mail directly to the email address provided by User. SAR strives
              to use commercially reasonable efforts to issue each ACE Alert
              prior to the market open on the subsequent trading day following
              the date that an Adverse Corporate Event (“ACE”) impacted the
              price of the common stock of the selected company based on the
              corresponding SAR database and event study analysis. However, SAR
              shall not be responsible or liable for any delay or failure to
              issue any ACE Alert (whether or not caused by or the fault of
              SAR). User understands and acknowledges that a selected company
              may become ineligible for ACE Alerts for reasons beyond SAR’s
              control (for example, without limitation, the selected company may
              cease being publicly traded), and in such event SAR shall not be
              liable or responsible for same and no refund of subscription fees
              will be issued. Each ACE Alert includes sixteen SAR data
              components for the selected company and, to the extent available
              based on the corresponding SAR database and event study analysis,
              presents the date and time of the corresponding corporate actions
              that triggered the ACE. An ACE Alert is only issued upon
              independent identification of any type of ACE (Type I, Type II or
              Hight Risk) according to SAR’s database and event study results.
              User understands and acknowledges that SAR does not provide
              qualitative analysis or commentary as part of the ACE Alerts or
              your ACE Alert subscription. Moreover, SAR shall not be required
              to provide any back-up documentation unless it is required by
              subpoena issued by a court of competent jurisdiction or by law.
              User further acknowledges and agrees that the ACE Alerts provided
              by SAR are administrative and technological in nature and that SAR
              is not providing investment or legal advice, or otherwise acting
              in an investment or legal advisory capacity, to User or anyone
              else. To the extent the ACE Alerts or your ACE Alert subscription
              are covered by any privacy or other regulation, User shall be
              solely and exclusively responsible for compliance with same. The
              ACE Alerts and the contents, data and information that comprises
              ACE Alerts is provided by SAR “AS IS” and “AS AVAILABLE”. SAR
              makes no representations or warranties, expressed or implied, with
              respect to the ACE Alerts (or the data and contents therein and
              reports generated thereby). SAR expressly disclaims the implied
              warranties of merchantability and fitness for a particular purpose
              respecting the ACE Alerts (and the data and contents therein and
              reports generated thereby). User understands and acknowledges that
              its use or reliance upon the ACE Alerts (and/or the data and
              contents therein and reports generated thereby) is at his/her sole
              risk and discretion. SAR is not responsible for any missed ACEs,
              failure to issue an ACE Alert or delays in issuing an ACE Alert.
              User understands and acknowledges that ACE Alerts are based solely
              on public information available to SAR through its raw data
              sources (which may include, without limitation, S&P Global Market
              Intelligence, ISS SCAS, FINRA, SEC’s EDGAR system and Amazon AWS
              Infrastructure) and processed by SAR in its sole and absolute
              discretion, and that ACE Alerts may not be comprehensive. User
              understands and acknowledges that the ACE Alerts (and the data and
              contents therein and reports generated thereby) are provided for
              informational purposes only, and, as such, same and any results
              therefrom shall not to be construed as an admission that anyone
              engaged in any wrongful, tortious, or unlawful activity, and no
              fraud or wrongdoing of any kind is alleged or implied by same or
              any such results derived therefrom. Upon termination or expiration
              of your subscription or account for any or no reason, all rights,
              licenses and subscriptions granted to User herein shall
              immediately terminate, and User’s access to the ACE Alerts and SAR
              Platform will be immediately terminated. User understands and
              acknowledges that ACE Alerts and/or the SAR Platform may not be
              available to or received by User due to: (i) scheduled system
              back-up or other on-going maintenance as determined to be
              appropriate by SAR in its sole and absolute discretion, or (ii)
              any unforeseen cause beyond SAR's reasonable control, including
              but not limited to Internet service provider or communications
              network failures, service interruption by cloud infrastructure
              provider, denial of service attacks or similar attacks, or any
              other force majeure events set forth in Section 31 below. User
              acknowledges that all right, title, ownership, and interest
              (including intellectual property rights) in and to the ACE Alerts
              (and the data and contents therein and reports generated thereby),
              including, without limitation, any of SAR’s software, codes,
              algorithms, sequences, derivative works, organization, structure,
              interfaces, any documentation, reports, data, copyrights, database
              rights, trade secrets, trade names, trademarks, logos, trade names
              (whether registered or unregistered), and the goodwill of the
              business associated with such trademarks or trade names, or other
              related materials (collectively, the “SAR IP”), is, and at all
              times shall remain, the sole and exclusive property of SAR. The
              SAR IP is protected by copyright and other intellectual property
              laws and treaties, and, as such, all right, title, and interest in
              and to the SAR IP is owned exclusively by SAR. Except the
              non-exclusive license to use the ACE Alerts as expressly provided
              and limited herein, the ACE Alert subscription does not grant to
              User, and shall not be deemed to grant to User, any ownership of,
              rights to or rights in, SAR IP, or any other ownership, rights or
              licenses with respect to the ACE Alerts (or the data and contents
              therein and reports generated thereby). FOR THE AVOIDANCE OF
              DOUBT: (I) THE ACE ALERTS (AND THE DATA AND CONTENTS THEREIN AND
              REPORTS GENERATED THEREBY) ARE THE SOLE AND EXCLUSIVE PROPERTY OF
              SAR; AND (II) USER’S PURCHASE OF AN ACE ALERT SUBSCRIPTION GRANTS
              USER A NON-EXCLUSIVE LICENSE TO USE SAME, SUBJECT TO THE TERMS.
            </p>
            <p>
              <b>12. ACE Alerts – Further Disclosure.</b> All content in ACE
              Alerts is subject to revision from time to time in SAR’s sole and
              absolute discretion. Source financial data used to perform the
              event study analysis to identify the ACEs and the estimate of
              market capitalization losses are publicly available and obtained
              from SAR’s raw data providers. No fraud or wrongdoing is alleged
              or implied by the information presented in ACE Alerts. User hereby
              agrees and acknowledges that any estimates or projections
              contained in ACE Alerts are only estimates or projections by SAR,
              that actual results will vary, and may vary substantially, from
              those estimates or projections by SAR and are based on many
              variables, assumptions and forecasts, many of which are beyond the
              control of SAR and any of which may present differences with
              estimates that are quantified using different techniques. Any
              statistical and quantitative analyses that form the basis of an
              ACE Alert related to potential violations of federal securities
              laws are based on ACEs as identified by SAR using the
              court-accepted event study methodology with the application of a
              two-year evaluation period, or a different evaluation period
              according to the data made available for that U.S. public company.
              A single-firm multivariate regression analysis with a minimum of
              100 observations (if a full 252 observations is unattainable) for
              a control period is applied to evaluate the statistical
              significance of the logarithmic residual stock price decline on
              the trading day(s) affected by the ACEs as identified by SAR.
              Stock price reaction is evaluated by estimating the statistical
              significance of the stock price decline in response to the ACE.
              Statistical significance is measured by computing the t-statistic
              of the residual stock price decline on the close-to-close trading
              session that is affected by the information disseminated via the
              ACE and may potentially constitute an alleged corrective in a
              securities class action claim for alleged violations of the
              Securities Exchange Act of 1934 or the Securities Act of 1933, or
              a shareholder derivate claim. This statistical exercise is also
              known as a t-test. If the t-statistic is greater than the
              t-statistic value of plus or minus 1.96, then the likelihood that
              the residual return could have been caused by non-company specific
              factors may be less than 5%. The applied control period ends a
              trading day prior to the start of the applied evaluation period,
              depending on data availability.
            </p>
            <p>
              <b>13. ACE Alert Confidentiality; Personal Use.</b> User agrees
              that ACE Alerts (and the content therein) constitute confidential
              information. User agrees to use reasonable efforts to ensure that
              the confidentiality of ACE Alerts (and the content therein)
              received by him/her is maintained. The ACE Alerts (and the content
              therein) shall be held in strict confidence by User and shall not
              be disclosed by User to any third parties without SAR’s prior
              written consent, except to the extent required by applicable law
              or a valid court or administrative order. Notwithstanding the
              foregoing, should User be required to disclose an ACE Alert (or
              the content therein) to comply with applicable law or a valid
              court or administrative order, then User shall: (i) promptly
              notify SAR in writing of the existence, terms and circumstances of
              such required disclosure (to the extent such notification is
              lawful); (ii) consult with SAR on the advisability of taking
              legally available steps to resist or narrow such disclosure; (iii)
              disclose only so much information as is required; and (iv) take
              all reasonable and lawful actions to obtain confidential treatment
              for such disclosure. Without limiting the foregoing
              confidentiality obligations: User hereby acknowledges and agrees
              that the ACE Alerts (and contents thereof) received by him/her are
              for User’s personal, non-commercial use only. User shall not
              distribute or share (including by email forward or otherwise) any
              ACE Alerts received by him/her or the contents thereof. Upon
              obtaining (and paying for, as applicable) an ACE Alert
              subscription, SAR grants User and User accepts from SAR, a
              limited, non-exclusive, non-transferable license (on the
              subscription level selected above) to receive ACE Alerts and
              license any constituent data that comprises such ACE Alerts solely
              for User’s personal, non-commercial use. User agrees that neither
              the ACE Alerts nor the contents thereof nor the data comprising
              same shall be used by User, directly or indirectly, on behalf of
              or to benefit third parties other than User. Nothing herein grants
              User the ability to resell the ACE Alerts, the contents thereof or
              the data that comprises same to third parties. User acknowledges
              and agrees that the ACE Alerts, the contents thereof and the data
              that comprises same is licensed (non-exclusively) by User and will
              remain solely owned by SAR. Upon termination or expiration of your
              subscription or account for any or no reason, you shall have the
              option of (i) permanently deleting all previously received ACE
              Alerts (and all copies thereof and derivatives, content and data
              therefrom) and providing written confirmation to SAR of such
              deletion; or (ii) entering into a separate written licensing
              agreement with SAR for the continued limited, non-exclusive,
              non-transferable license to use the data that comprises any ACE
              Alerts(s) on <a href="https://www.sarlit.com/terms-conditions" target="_blank" rel="noopener noreferrer">Terms and Conditions</a> that SAR deems appropriate in
              its sole and absolute discretion.
            </p>
            <p>
              <b>14. Third Party Payment Processor.</b> You understand and
              acknowledge that SAR uses a third-party payment processor to
              process your payment(s) for our Products. You represent and
              warrant that you are an authorized user of the payment method
              provided by you for the purchase of our Products. Your payment
              information (such as credit card information) is provided directly
              to said third-party payment processor, which may have its own
              terms of use, <a href="https://www.sarlit.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and/or other terms, conditions or
              policies in effect. SAR does not receive or store your payment
              information and is therefore not responsible for same. You
              expressly agree that: (i) SAR is not responsible for any action or
              inaction by said third-party payment processor; and (ii) SAR shall
              not be liable to you for any loss, harms, liability, costs, fees,
              fines, expenses or damages with respect to your payment
              information (including, without limitation, the unauthorized leak,
              release or disclosure of same). You hereby agree to indemnify,
              defend and hold harmless SAR from and against any loss, harms,
              liability, costs, fees, fines, expenses or damages with respect to
              your payment information (including, without limitation, the
              unauthorized leak, release or disclosure of same) and/or any
              action or inaction by said third-party payment processor.
            </p>
            <p>
              <b>15. Prohibited Actions.</b> User shall not: (a) share,
              distribute, lend, lease, sublicense or otherwise make available
              the Products (including ACE Alerts and the contents thereof), as
              available, in any manner whatsoever, with or to any third party;
              (b) reverse engineer, decompile or disassemble the Products or
              Websites, as available, or attempt to locate or obtain its or
              their source code; (c) attempt to alter or remove any trademark,
              copyright or other proprietary notice contained within the
              Products or Websites, as available; (d) make use of the Products
              or Websites, as available, in any manner not in accordance with
              the Terms; or (e) violate any applicable laws, rules or
              regulations in connection with User’s access or use of the
              Products or Websites, as available.
            </p>
            <p>
              <b>16.Limitation on Liability.</b> Our Websites and Products are
              provided on an “as is” and “as available” basis, and we make no
              guarantees that they always will be safe, secure, uninterrupted or
              error-free, or that they will function without disruptions,
              delays, or imperfections. User covenants, represents and warrants
              that it accepts sole and complete responsibility for: (a) the use
              of the Websites and Products in accordance with the Terms to
              achieve User’s intended results; and (b) the results obtained from
              the Websites and Products. WE DISCLAIM ALL WARRANTIES WITH RESPECT
              TO THE WEBSITES AND PRODUCTS, WHETHER EXPRESS OR IMPLIED,
              INCLUDING THE IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, TO THE MAXIMUM
              EXTENT PERMITTED BY LAW. To the maximum extent permitted by law,
              in no event shall we be liable to you for any lost profits,
              revenues, information, or data, or consequential, special,
              indirect, exemplary, punitive, incidental or like damages
              whatsoever (including, without limitation, damages for loss of
              business profits, business interruption, loss of business
              information, or any other pecuniary loss) arising out of or
              related to the Terms, our Websites or our Products (or your use of
              or inability to use same), even if we have been advised of the
              possibility of such damages. IN ADDITION (WITHOUT LIMITING THE
              FOREGOING), YOU HEREBY ACKNOWLEDGE AND AGREE THAT OUR AGGREGATE
              LIABILITY TO YOU ARISING OUT OF OR RELATING TO THE TERMS, OUR
              WEBSITES OR OUR PRODUCTS (OR YOUR USE OF OR INABILITY TO USE
              SAME), WHETHER FOR ANY LOSS, HARMS, LIABILITY, COSTS, EXPENSES,
              DAMAGES OR OTHERWISE, WILL NOT EXCEED THE LESSER OF: (I) $25,000;
              OR (II) THE TOTAL SUBSCRIPTION FEES PAID BY YOU TO SAR DURING THE
              TWELVE (12) MONTHS PRECEDING THE CLAIM DATE. The foregoing
              limitation of liability shall apply regardless of our negligence,
              breach of the Terms, strict liability, non-compliance with law or
              other fault. You specifically acknowledge that our liability for
              damages suffered by you are limited and liquidated as set forth
              herein. To the maximum extent permitted by law, you agree to waive
              any and all protections available to you under applicable law,
              including, but not limited to, general release protections. You
              acknowledge and agree that each provision of the Terms that sets
              forth a limitation of our liability, disclaimer of warranties
              and/or damages exclusion is a necessary basis of the bargain
              between you and us, and that the Websites and Products would not
              be offered or made available to you without same.
            </p>
            <p>
              <b>17. Legal Actions. </b>EXCEPT WHERE PROHIBITED BY LAW, NO CLAIM
              OR LEGAL ACTION THAT YOU MAY HAVE ARISING OUT OF THE TERMS, OUR
              WEBSITES OR OUR PRODUCTS (OR YOUR USE OF OR INABILITY TO USE
              SAME), WHETHER BASED ON AGREEMENT, STRICT LIABILITY, TORT,
              NEGLIGENCE OR OTHERWISE, MAY BE BROUGHT MORE THAN ONE YEAR AFTER
              THE DATE THE CAUSE OF ACTION FOR SUCH CLAIM ACCRUED.
            </p>
            <p>
              <b>18. Forum Selection; Waiver of Jury Trial.</b> Any legal or
              equitable claim or cause of action relating to or arising out of
              the Terms, our Websites or our Products (or your use of or
              inability to use same) (referred to as a “Claim”) shall be brought
              exclusively in the Maryland state courts sitting in Montgomery
              County, Maryland or the U.S. District Court for the District of
              Maryland, Greenbelt Division (to the express exclusion of all
              other jurisdictions). You hereby irrevocably agree and consent
              that such forum is convenient and has jurisdiction over the
              parties to hear and decide any and all such Claims and you waive
              any argument that such court is improper, or such jurisdiction or
              forum is inconvenient. For any Claim, the substantially prevailing
              party’s reasonable costs, necessary disbursements and attorneys'
              fees incurred shall be paid for by the other party. YOU HEREBY
              IRREVOCABLY WAIVE, TO THE FULLEST EXTENT PERMITTED BY APPLICABLE
              LAW, ANY RIGHT YOU MAY HAVE: (I) TO A TRIAL BY JURY IN ANY LEGAL
              PROCEEDING DIRECTLY OR INDIRECTLY ARISING OUT OF OR RELATING TO A
              CLAIM; AND (II) TO PARTICIPATE IN A CLASS OR REPRESENTATIVE ACTION
              DIRECTLY OR INDIRECTLY ARISING OUT OF OR RELATING TO A CLAIM.
            </p>
            <p>
              <b>19. Governing Law.</b> The Terms, our Websites and our Products
              (and your use of or inability to use same) will be governed by
              applicable Federal laws and the laws of the state of Maryland,
              without regard to conflict of law provisions. Maryland’s Uniform
              Computer Information Transactions Act shall not apply.
            </p>
            <p>
              <b>20. Entire Agreement; Severability.</b> The Terms constitute
              the entire agreement between the parties with respect to the
              subject matter contained therein. You acknowledge that we are not
              bound by and you have not relied on any representation, promise,
              condition, inducement or warranty (express or implied) not
              included in the Terms. If any provision in the Terms is determined
              to be invalid or unenforceable, the remaining provisions will
              remain in full force and effect. In the event that a provision of
              the Terms is determined to be invalid or unenforceable, said
              provision will be modified to the minimum extent necessary to
              render it valid and enforceable. Said modified provision will be
              in full force and effect as if it were originally part of the
              Terms. Any waiver of the Terms must be expressly made in writing,
              signed by us. Failure to strictly enforce any provision of the
              Terms does not constitute a waiver. A waiver of any part of the
              Terms in one instance is not a waiver of any other part or any
              other instance.
            </p>
            <p>
              <b>21. Remedies.</b> In the event User breaches or attempts or
              threatens to breach any of the provisions of the Terms, User
              hereby acknowledges that monetary remedies may be inadequate to
              fully compensate SAR for such breach or attempted or threatened
              breach. Accordingly, in such case, and not excluding any other
              remedy at law or in equity or under the Terms, SAR shall have the
              right, in addition to such other remedies that may be available,
              to injunctive relief enjoining such breach or attempted or
              threatened breach by User without the requirement to show actual
              damages or post a bond or other security.
            </p>
            <p>
              <b>22. Software.</b> We are not responsible or liable for any
              damages to or changes in the performance of your computer or
              device following access or use of our Websites or Products.
            </p>
            <p>
              <b>23. Updating Terms.</b> We have the right to update, modify or
              amend the Terms from time to time, within our sole and absolute
              discretion. Once any updated, modified or amended Terms are in
              effect, they will be posted at www.sarlit.com and you will be
              bound by them if you continue to use or access any of our Websites
              or purchase or use any of our Products. The most recent version of
              the Terms in effect will always be posted at www.sarlit.com. If
              you do not agree with any updated, modified or amended Terms, you
              must immediately cease: (i) using or accessing any of our
              Websites; and (ii) receiving, purchasing or using any of our
              Products.
            </p>
            <p>
              <b>24. Assignability.</b> You will not transfer any of your rights
              or obligations under the Terms to anyone else without our prior
              written consent. The Terms do not confer any third-party
              beneficiary rights. We may assign this contract, within our sole
              and absolute discretion, without obtaining your consent or
              providing you with any notice.
            </p>
            <p>
              <b>25. All Rights Reserved.</b> We reserve all rights not
              expressly granted to you.
            </p>
            <p>
              <b>26. Indemnity.</b> IF ANY THIRD PARTY FILES ANY CLAIM OR LEGAL
              ACTION AGAINST US OR ANY OTHER PERSON OR ENTITY AUTHORIZED TO ACT
              ON OUR BEHALF, ARISING FROM OR RELATED TO, IN WHOLE OR IN PART,
              DIRECTLY OR INDIRECTLY, ANY ACT OR OMISSION OF YOU, YOUR USE OR
              ACCESS OF OUR WEBSITES, YOUR PURCHASE OR USE OF OUR PRODUCTS (OR
              ANY CONTENT, RESULTS, REPORTS, INFORMATION OR DATA DERIVING
              THEREFROM), YOUR VIOLATION OF ANY APPLICABLE LAW, RULE OR
              REGULATION OR YOUR VIOLATION OF THE TERMS, THEN YOU AGREE TO
              INDEMNIFY, DEFEND AND HOLD US (AND ANY OTHER PERSON OR ENTITY
              AUTHORIZED TO ACT ON OUR BEHALF) COMPLETELY HARMLESS FROM ANY SUCH
              ACTIONS OR CLAIMS, INCLUDING ALL DAMAGES, EXPENSES, COSTS, AND
              REASONABLE ATTORNEYS' FEES WE INCUR.
            </p>
            <p>
              <b>27. Headings.</b> The headings in the Terms are for convenience
              of reference only and do not control or affect the contents of any
              provision contained herein.
            </p>
            <p>
              <b>28. Notices.</b> Except as otherwise expressly provided herein,
              any notice required to be sent to us under or pursuant to the
              Terms must be sent by certified mail to SAR, LLC, 4720 Montgomery
              Lane, Suite 410, Bethesda, MD 20814, with a copy to our current
              Resident Agent as listed in the public records of the Maryland
              State Department of Assessments and Taxation. Any notice required
              or permitted to be sent to you under or pursuant to the Terms will
              be sent to the address or email address you provide. If you fail
              to provide an address or email address, any required notices will
              automatically be deemed timely given to you.
            </p>
            <p>
              <b>29. No Interpretations Against Drafter. </b>The Terms shall not
              be interpreted or construed against a party by reason of that
              party being the drafter.
            </p>
            <p>
              <b>30. No Partnership.</b> Nothing in the Terms or otherwise is
              intended to or shall operate to create a partnership between User
              and SAR or authorize either party to act as an agent for the
              other, and neither party shall have the authority to act in the
              name or on behalf of or otherwise bind the other in any way.
            </p>
            <p>
              <b>31. Force Majeure.</b> We are not liable or responsible for any
              failure, delay or interruption on our part with respect to our
              Websites, our Products or our compliance with the Terms due, in
              whole or in part, directly or indirectly, to any reasons or
              circumstances beyond our reasonable control (including, without
              limitation, fire, flood, storm, acts of God, war, strikes, labor
              shortages, casualty, terrorism, severe weather, epidemics,
              pandemics, COVID-19, supply chain issues, material shortages,
              failure of a utility service or transport or telecommunications
              network, governmental orders, riots, boycotts, embargoes or
              otherwise).
            </p>
            <p>
              <b>32. Intellectual Property Claims.</b> We respond to notices of
              alleged copyright infringement under the Digital Millennium
              Copyright Act (“DMCA”). If you believe your intellectual property
              rights have been infringed, please contact us at info@sarlit.com.
            </p>
            <p>
              <b>33. Notice to California Residents.</b> Under California Civil
              Code Section 1789.3, California users receive the following
              consumer rights notice – For complaints or further information, we
              can be contacted in writing at SAR, LLC, 4720 Montgomery Lane,
              Suite 410, Bethesda, MD 20814, or by telephone at 202-436-9994.
              The Complaint Assistance Unit of the Division of Consumer Services
              of the California Department of Consumer Affairs may be contacted
              in writing at 1625 North Market Blvd., Suite N112, Sacramento, CA
              95834, or by telephone at 800-952-5210.
            </p>
          </div>
        </div>
      </div>
      <br />
    </div>
  );
};

export default TermsAndConditions;



// WEBPACK FOOTER //
// ./src/ace/TermsAndConditions.js